import {memo, useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useLocation} from '@s-ui/react-router'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'

import CardAdPriceIndicator, {cardAdPriceIndicatorSizes} from './CardAdPriceIndicator/index.js'
import {CardAdPriceCash} from './card-ad-price-cash.js'
import {CardAdPriceFinanced} from './card-ad-price-financed.js'
import {CardAdPriceFinancedPrivate} from './card-ad-price-financed-private.js'
import {CardAdPriceInfo} from './card-ad-price-info.js'
import {CardAdPriceMonthlyFee} from './card-ad-price-monthly-fee.js'
import {CardAdPriceSubscription} from './card-ad-price-subscription.js'
import {CardAdPriceWarranty} from './card-ad-price-warranty.js'
import {MONTHS_IN_YEAR} from './utils.js'

const BASE_CLASS = 'mt-CardAdPrice'

const _getCardAdPriceClass = ({isExtended, hasSpace, vertical}) => {
  return cx(BASE_CLASS, {
    [`${BASE_CLASS}--extended`]: isExtended,
    [`${BASE_CLASS}--space`]: hasSpace,
    [`${BASE_CLASS}--vertical`]: vertical
  })
}

const EMPTY_FINANCED_PRICE = 0

const CURRENT_YEAR = new Date().getFullYear()
const MAX_YEARS_OLD = 10
const getHasPriceWithinFinancingRange = price => {
  return price >= 1667 && price <= 70000
}

const CardAdPrice = ({
  ad,
  forcedShowPriceIndicator,
  handleScrollToElements = {},
  handleScrollToContactForm,
  handleScrollToFinancingTerms,
  hasFinancingData = false,
  hasFootNote = false,
  instalmentPrice,
  isExtended = false,
  isGridLayout = false,
  isPreview,
  subscriptionPriceOption,
  vertical
}) => {
  const {i18n, domain} = useContext(Context)
  const {query} = useLocation()
  const hasForceCriticalCSS = Boolean(query?.forceCriticalCSS) && JSON.parse(query.forceCriticalCSS)

  const {
    handleScrollToB2cFinancingPanel = () => {},
    handleScrollToC2cPanel = () => {},
    handleScrollToPriceIndicatorPanel = () => {}
  } = handleScrollToElements

  const config = domain.get('config')
  const isMobile = config.get('isMobile')

  const initialMediaQueryMatch = {
    defaultMatches: !isMobile
  }

  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, initialMediaQueryMatch)

  const {HAS_FINANCING_PRI_ENABLED} = config.get('financing')

  const {taxTypes} = config.get('search')
  const {SHOW_CUSTOM_MONTHLY_FEE, SHOW_WARRANTY_FREE_TEXT, HIDE_TAXES} = config.get('search')

  const {
    financedPrice,
    financingData,
    hasWarranty,
    hasOfficialWarranty,
    includesTaxes,
    isFinanced,
    isProfessional,
    monthlySubscriptionFee,
    monthlyFee,
    offer,
    offerType,
    price,
    priceAverageIndicator,
    priceDrop: priceDropData,
    priceRankIndicator,
    subscriptionPriceOptions,
    targetBuyer,
    taxTypeId,
    warrantyMonths,
    warrantyText,
    year
  } = ad
  const {id: offerTypeId} = offerType || {}

  const isRenting = Boolean(ad.monthlySubscriptionFee)
  const instalmentInfoAvailable = Boolean(instalmentPrice)
  const hasPriceWithinFinancingRange = getHasPriceWithinFinancingRange(price)
  const isConsideredNew = CURRENT_YEAR - year <= MAX_YEARS_OLD

  const showFinancedPricePrivate =
    !isProfessional &&
    hasPriceWithinFinancingRange &&
    isConsideredNew &&
    instalmentInfoAvailable &&
    HAS_FINANCING_PRI_ENABLED &&
    isExtended

  const monthlyFeeFormatted = monthlyFee ? i18n.c(monthlyFee) : null

  const hasFinancedPrice = isFinanced && financedPrice && financedPrice > EMPTY_FINANCED_PRICE

  const computedPriceValue = financingData?.computedPrice || financedPrice
  const currentFinancedPrice = computedPriceValue || financedPrice

  const showFinancedPrice = isProfessional && instalmentInfoAvailable

  const _getTaxType = () => {
    if (isPreview && !HIDE_TAXES)
      return includesTaxes
        ? `${i18n.t('CARD_AD_GENERIC_TAXES_INCLUDED')}`
        : `${i18n.t('CARD_AD_GENERIC_NOT_TAXES_INCLUDED')}`

    const taxType = taxTypes.find(({id}) => id === taxTypeId)

    if (!taxType) return null

    return includesTaxes
      ? `${taxType.label} ${i18n.t('CARD_AD_INCLUDED')}`
      : `${taxType.label} ${i18n.t('CARD_AD_NOT_INCLUDED')}`
  }

  const _getWarrantyPeriod = ({months, forceMonths = false}) => {
    if (forceMonths && !months) return `- ${i18n.t('COMMON.PLURALS.MONTH', 1)}`
    if (forceMonths) return i18n.t('COMMON_N_MONTHS', months)
    if (!months) return `- ${i18n.t('COMMON.PLURALS.YEAR', 1)}`

    const showYear = months % MONTHS_IN_YEAR === 0
    return showYear ? i18n.t('COMMON_N_YEARS', months / MONTHS_IN_YEAR) : i18n.t('COMMON_N_MONTHS', months)
  }

  const _getWarrantyLiteral = () => {
    return hasWarranty && warrantyMonths
      ? `${i18n.t('CARD_AD_WARRANTY')} ${_getWarrantyPeriod({
          months: warrantyMonths
        })}`
      : i18n.t('CARD_AD_NO_WARRANTY')
  }

  const taxType = _getTaxType()

  const warrantyLiteral =
    SHOW_WARRANTY_FREE_TEXT || warrantyText ? i18n.t('CARD_AD_WARRANTY_FREE_TEXT') : _getWarrantyLiteral()

  const showWarranty = !isRenting

  const showMonthlyFee = Boolean(monthlyFee) && isProfessional && isExtended && SHOW_CUSTOM_MONTHLY_FEE

  const hasPriceDrop = Boolean(priceDropData)

  const showPriceInfo = isProfessional && showWarranty && taxType && !monthlySubscriptionFee

  const showCardPriceWarranty = showPriceInfo && isExtended && hasWarranty && !monthlySubscriptionFee

  const isWithoutWarranty = !hasWarranty || !(warrantyMonths || isPreview)

  const hasPriceRankIndicator = Boolean(priceRankIndicator)
  const hasBothTargetBuyerOffers = Boolean(ad.subscriptionBusinessAdInfo)
  const showPriceRankIndicator = hasPriceRankIndicator && isExtended && !isRenting

  const handleClickPriceIndicator = () => handleScrollToPriceIndicatorPanel()

  return (
    <div
      className={_getCardAdPriceClass({
        isExtended,
        hasSpace: Boolean(monthlySubscriptionFee && !isHigherThanMobile),
        vertical
      })}
    >
      {price ? (
        <CardAdPriceCash
          forcedShowPriceIndicator={forcedShowPriceIndicator}
          includesTaxes={includesTaxes}
          isExtended={isExtended}
          isGridLayout={isGridLayout}
          offer={offer}
          price={price}
          priceDropData={priceDropData}
          priceRankIndicator={priceRankIndicator}
          showPriceInfo={showPriceInfo}
          taxType={taxType}
          vertical={vertical}
        />
      ) : (
        <></>
      )}
      {(showFinancedPricePrivate || hasForceCriticalCSS) && (
        <CardAdPriceFinancedPrivate
          ad={ad}
          handleScrollToC2cPanel={handleScrollToC2cPanel}
          hasForceCriticalCSS
          instalmentPrice={instalmentPrice}
          isExtended={isExtended}
          isTopSpaced={hasPriceRankIndicator && !isExtended}
          showCta
          vertical={vertical}
        />
      )}
      {showFinancedPrice && (
        <CardAdPriceFinanced
          financedPrice={currentFinancedPrice || price}
          handleScrollToB2cFinancingPanel={handleScrollToB2cFinancingPanel}
          handleScrollToContactForm={handleScrollToContactForm}
          handleScrollToFinancingTerms={handleScrollToFinancingTerms}
          instalmentPrice={instalmentPrice}
          isExtended={isExtended}
          isGridLayout={isGridLayout}
          isTopSpaced={hasPriceRankIndicator && !isExtended}
          vertical={vertical}
        />
      )}
      {monthlySubscriptionFee && (
        <CardAdPriceSubscription
          vertical={vertical}
          isExtended={isExtended}
          isMobile={!isHigherThanMobile}
          isPreview={isPreview}
          hasBothTargetBuyerOffers={hasBothTargetBuyerOffers}
          hasFinancedPrice={hasFinancedPrice}
          hasFootNote={hasFootNote}
          monthlySubscriptionFee={monthlySubscriptionFee}
          priceRankIndicator={priceRankIndicator}
          subscriptionPriceOption={subscriptionPriceOption}
          subscriptionPriceOptions={subscriptionPriceOptions}
          targetBuyer={targetBuyer}
        />
      )}

      {(showPriceInfo || warrantyText || hasPriceDrop) && !isExtended && (
        <CardAdPriceInfo
          priceDropData={priceDropData}
          vertical={vertical}
          warrantyLiteral={warrantyLiteral}
          showWarranty={showWarranty}
          taxType={taxType}
          includesTaxes={includesTaxes}
          isProfessional={isProfessional}
          isWithoutWarranty={isWithoutWarranty}
        />
      )}
      {showMonthlyFee && (
        <CardAdPriceMonthlyFee
          ad={ad}
          handleScrollToContactForm={handleScrollToContactForm}
          handleScrollToFinancingTerms={handleScrollToFinancingTerms}
          hasFinancedPrice={hasFinancedPrice}
          hasFinancingData={hasFinancingData}
          monthlyFee={monthlyFeeFormatted}
          vertical={vertical}
        />
      )}
      {showCardPriceWarranty && (
        <CardAdPriceWarranty
          vertical={vertical}
          hasWarranty={hasWarranty}
          hasOfficialWarranty={hasOfficialWarranty}
          warrantyLiteral={warrantyLiteral}
          warrantyMonths={warrantyMonths}
          isPreview={isPreview}
        />
      )}
      {showPriceRankIndicator && (
        <CardAdPriceIndicator
          average={priceAverageIndicator}
          offerTypeId={offerTypeId}
          onClick={handleClickPriceIndicator}
          rank={priceRankIndicator}
          size={cardAdPriceIndicatorSizes.medium}
        />
      )}
    </div>
  )
}

CardAdPrice.displayName = 'CardAdPrice'

CardAdPrice.propTypes = {
  ad: PropTypes.object.isRequired,
  forcedShowPriceIndicator: PropTypes.bool,
  handleScrollToContactForm: PropTypes.func,
  handleScrollToElements: PropTypes.object,
  handleScrollToFinancingTerms: PropTypes.func,
  hasFinancingData: PropTypes.bool,
  hasFootNote: PropTypes.bool,
  instalmentPrice: PropTypes.number,
  isExtended: PropTypes.bool,
  isGridLayout: PropTypes.bool,
  isPreview: PropTypes.bool,
  subscriptionPriceOption: PropTypes.object,
  vertical: PropTypes.bool
}

const MemoCardAdPrice = memo(CardAdPrice)
MemoCardAdPrice.displayName = 'CardAdPrice'
export default MemoCardAdPrice
export {CardAdPriceIndicator, cardAdPriceIndicatorSizes}
